/**
 * Chart primitives, in plain SVG.
 *
 * No chart library: these four forms are simple enough that a dependency would
 * cost more than it saves, and hand-rolled SVG keeps the marks on the brand
 * tokens rather than a library's defaults.
 *
 * Series colours come from --chart-series-* and are validated per theme (see
 * design-system/tokens.css). Nothing here picks a colour inline.
 */
import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from "react";
import { InfoHint } from "@/components/shared/info-hint";

export const SERIES_1 = "var(--chart-series-1)";
export const SERIES_2 = "var(--chart-series-2)";
export const BENCHMARK = "var(--chart-benchmark)";
export const GRID = "var(--chart-grid)";

export interface Point {
  label: string;
  value: number | null;
  /** Anything the caller needs on click -- a period id, a demand id. */
  id?: string;
  /** Unclosed periods are forecast and are drawn differently, never silently. */
  provisional?: boolean;
}

/** Nice round axis bounds, so gridlines land on readable numbers. */
export function niceBounds(values: number[], includeZero = true): [number, number] {
  const present = values.filter((v) => Number.isFinite(v));
  if (present.length === 0) return [0, 1];
  let min = Math.min(...present, includeZero ? 0 : Math.min(...present));
  let max = Math.max(...present, includeZero ? 0 : Math.max(...present));
  if (min === max) {
    max = min + 1;
  }
  const span = max - min;
  const step = 10 ** Math.floor(Math.log10(span || 1));
  min = Math.floor(min / step) * step;
  max = Math.ceil(max / step) * step;
  return [min, max];
}

export function ChartFrame({
  title,
  subtitle,
  legend,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  legend?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <figure className="w-full space-y-1.5">
      {/* Legend on the title's row, not stacked beneath it: a third full-width
          line of chrome pushed every plot down for no information gain. It
          wraps under the title on a narrow card rather than crushing it. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <figcaption className="flex items-center gap-1.5">
          <h3 className="font-display text-sm font-semibold text-foreground">{title}</h3>
          {subtitle ? <InfoHint text={subtitle} data-chart-help /> : null}
        </figcaption>
        {legend}
      </div>
      {children}
      {footer ? <div className="text-xs text-muted-foreground">{footer}</div> : null}
    </figure>
  );
}

/** A legend is always present for two or more series -- identity is never colour alone. */
export function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-4">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-[2px]"
            style={{ background: item.color }}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

export function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
      {message}
    </div>
  );
}

/**
 * The width to draw an SVG chart at, measured from its container.
 *
 * The charts lay out in user units and the browser scales the viewBox to the
 * container. With a fixed 720-unit canvas inside a 1600px card everything,
 * labels included, draws at 2.2x: a page that reads as zoomed in. Sizing the
 * canvas to the container keeps one unit at one pixel, so text stays its own
 * size and the plot alone grows with the card.
 *
 * Falls back to `fallback` until measured and wherever ResizeObserver is
 * missing (jsdom), so tests see the same canvas they always did.
 */
export function useChartWidth(fallback = 720): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, Math.max(width, 420)];
}
